import { Event } from 'store/api/event';
import { IdObject, Nullable } from 'types';
import { Carrier } from 'store/api/carriers';
import { Product } from 'store/api/product/types';
import { Supplier } from 'store/api/supplier/types';

export enum StockOrderStatus {
  Pending = 'Pending',
  Shipped = 'Shipped',
  InTransit = 'InTransit',
  Exception = 'Exception',
  Shipment = 'Shipment',
  Done = 'Done',
}

export const StockOrderStatusLabel = {
  [StockOrderStatus.Done]: 'Done',
  [StockOrderStatus.Pending]: 'Pending',
  [StockOrderStatus.Shipped]: 'Shipped',
  [StockOrderStatus.Shipment]: 'Shipment',
  [StockOrderStatus.Exception]: 'Exception',
  [StockOrderStatus.InTransit]: 'In Transit',
};

export interface EditStockOrderFormValues
  extends
    Partial<
      Omit<
        StockOrder,
        | 'id'
        | 'date'
        | 'items'
        | 'carrier'
        | 'dateShipped'
        | 'estimatedDeliveryDate'
      >
    >,
    Pick<StockOrder, 'id'> {
  date?: Date;
  eventId?: number;
  carrierId?: number;
  supplierId?: number;
  dateShipped?: Date | null;
  eventIds?: number[] | null;
  recipient?: 'event' | 'storage';
  orderTo?: 'storage' | 'terminal';
  estimatedDeliveryDate?: Date | null;
  items?: {
    count: string;
    price: number;
    productId: number;
    totalPrice: number;
  }[];
}

export interface StockOrder extends IdObject {
  date: string;
  amount: string;
  number: string;
  events: Event[];
  carrier?: Carrier;
  createdBy: number;
  ltlPrice?: string;
  storage?: Storage;
  workOrder?: string;
  supplier: Supplier;
  event?: Event | null;
  dateShipped?: string;
  phone?: string | null;
  trackingNumber: string;
  linkToWebsite?: string;
  address?: string | null;
  storageId: number | null;
  status: StockOrderStatus;
  items: StockOrderProduct[];
  contactName?: string | null;
  estimatedDeliveryDate: string;
  emergencyPhone?: string | null;
  // Russell Logistics invoice # for the LTL load (Invoice # = Load #).
  ltlInvoiceNumber?: string | null;
}

export interface StockOrderProduct extends IdObject {
  count: number;
  cases: number;
  price: number;
  units: number;
  product: Product;
  productId: number;
  isShippingOnly?: Nullable<boolean>;
}

export interface StockOrderCompleteRequest extends IdObject {
  eventId?: number;
}

export type CreateStockOrderFormValues = Omit<EditStockOrderFormValues, 'id'>;

// L4a: PATCH inventory/stockorder/:id/ltl and inventory/transfer/:id/ltl set
// only these two columns. A body without either field writes nothing and
// answers the current values (used to learn whether Main has the route).
export interface StockLtlFields extends Partial<StockShippingFields> {
  id: number;
  status: string;
  ltlPrice: string | null;
  ltlInvoiceNumber: string | null;
}

// T-F07 (owner 2026-10-15): the stock order route (not the transfer one) also
// sets and answers the shipment fields. '' = not set (NOT NULL columns).
export interface StockShippingFields {
  dateShipped: string;
  trackingNumber: string;
  carrierId: number | null;
  estimatedDeliveryDate: string;
}

export interface StockLtlEditRequest extends Partial<StockShippingFields> {
  id: number;
  ltlPrice?: string | null;
  ltlInvoiceNumber?: string | null;
}
